import {computed, inject, Injectable, signal} from "@angular/core";
import {toObservable} from "@angular/core/rxjs-interop";
import {CookieService} from "ngx-cookie-service";
import {BehaviorSubject, combineLatest, filter, forkJoin, map, of, shareReplay, startWith, switchMap,} from "rxjs";
import {ApiService} from "src/app/services/api.service";
import {UserService} from "src/app/services/user.service";
import {MalfunctionStatus} from "src/app/shared/interfaces/report";
import {Userrole} from "src/app/shared/interfaces/user";

interface WorkplaceAccessState {
  selectedProductionLine: {
    id: string;
    name: string;
  } | null;
  selectedWorkplace: {
    id: string;
    name: string;
  } | null;
  currentLevel: Level;
}

type Level = "all" | "productionLine";

@Injectable({
  providedIn: "root",
})
export class WorkplaceAccessService {
  private userService = inject(UserService);
  private apiService = inject(ApiService);
  private cookieService = inject(CookieService);

  Userrole = Userrole;

  // sources
  private currentLevel = signal<Level>("all")
  public selectedProductionLine$ = new BehaviorSubject<{
    id: string;
    name: string;
  } | null>(null);
  public selectedWorkplace$ = new BehaviorSubject<{
    id: string;
    name: string;
  } | null>(null);
  userrole = computed(() => this.userService.userrole());
  private userrole$ = toObservable(this.userrole);
  private productionLines$ = this.userrole$.pipe(
    switchMap((role) => {
      switch (role) {
        case Userrole.Teamleiter:
        case Userrole.Schichtleiter:
          const lineId = this.userService.userProductionLineId();
          console.log("productionLine", lineId);
          this.setCurrentLevel('all')
          return this.apiService
            .getProductionLinesAdmin()
            .pipe(map((lines) => lines.filter((line) => line.id === lineId)));
        case Userrole.Mitarbeiter:
          this.setCurrentLevel('productionLine')
          const workerLineId = this.cookieService
            .get("productionLine")
            .split("%")[0];
          return this.apiService
            .getProductionLinesAdmin()
            .pipe(
              map((lines) => lines.filter((line) => line.id === workerLineId))
            );
        default:
          this.setCurrentLevel('all');
          return this.apiService.getProductionLinesAdmin();
      }
    }),
    switchMap((productionLines) => {
      return forkJoin(
        productionLines.map((line) => {
          return forkJoin({
            productionLine: of(line),
            activeReports: this.apiService
              .getFilteredReports({
                productionLineFilter: Array(line.id!),
                statusFilter: [MalfunctionStatus.RECORDED, MalfunctionStatus.ESCALATED],
              })
              .pipe(map((response) => response.reportData || [])),
            reports: this.apiService
              .getFilteredReports({
                productionLineFilter: Array(line.id!),
                statusFilter: [MalfunctionStatus.SOLVED],
                rangeEnd: 5,
              })
              .pipe(map((response) => response.reportData || [])),
          }).pipe(
            map(({productionLine, activeReports, reports}) => ({
              ...productionLine,
              activeReports,
              reports,
              escalatedReports: activeReports.filter((report) => report.status === MalfunctionStatus.ESCALATED)
            }))
          );
        })
      );
    }),
    shareReplay(1)
  );

  private workplaces$ = combineLatest([
    this.selectedProductionLine$,
    this.userrole$,
  ]).pipe(
    filter(([selectedLine, role]) => selectedLine !== null),
    switchMap(([selectedLine, role]) => {
      if (!selectedLine) return of([]);
      return this.apiService.getWorkstationsAdmin().pipe(
        map((workplaces) => {
          const filteredWorkplaces = workplaces.filter(
            (w) => w.production_line.id === selectedLine.id
          );
          if (role === Userrole.Mitarbeiter) {
            const workstationId = this.cookieService
              .get("workplace")
              .split("%")[0];
            return filteredWorkplaces.filter((w) => w.id === workstationId);
          }
          return filteredWorkplaces;
        })
      );
    }),
    switchMap((workplaces) => {
      return forkJoin(
        workplaces.map((workplace) => {
          return forkJoin({
            productionLine: of(workplace),
            activeReports: this.apiService
              .getFilteredReports({
                workplaceFilter: Array(workplace.id!),
                statusFilter: [MalfunctionStatus.RECORDED, MalfunctionStatus.ESCALATED],
              })
              .pipe(map((response) => response.reportData || [])),
            reports: this.apiService
              .getFilteredReports({
                workplaceFilter: Array(workplace.id!),
                statusFilter: [MalfunctionStatus.SOLVED],
                rangeEnd: 5,
              })
              .pipe(map((response) => response.reportData || [])),
          }).pipe(
            map(({productionLine, activeReports, reports}) => ({
              ...productionLine,
              activeReports,
              reports,
              escalatedReports: activeReports.filter((report) => report.status === MalfunctionStatus.ESCALATED)
            }))
          );
        })
      );
    })
  );

  vm$ = combineLatest([
    this.productionLines$,
    this.selectedProductionLine$,
    this.userrole$,
  ]).pipe(
    switchMap(([productionLines, selectedLine, role]) => {
      const showProductionLines = role !== Userrole.Mitarbeiter;

      // If we have a selected line, load workplaces
      if (selectedLine) {
        return this.workplaces$.pipe(
          map((workplaces) => ({
            productionLines,
            selectedLineId: selectedLine.id,
            workplaces,
            showProductionLines,
            isLoading: false,
          })),
          startWith({
            productionLines,
            selectedLineId: selectedLine.id,
            workplaces: [],
            showProductionLines,
            isLoading: true,
          })
        );
      }
      return of({
        productionLines,
        selectedLineId: null,
        workplaces: [],
        showProductionLines,
        isLoading: false,
      });
    })
  );

  // state
  private state = signal<WorkplaceAccessState>({
    selectedProductionLine: null,
    selectedWorkplace: null,
    currentLevel: 'productionLine',
  });

  // selector
  selectedProductionLineSignal = computed(
    () => this.state().selectedProductionLine
  );
  selectedWorkplaceSignal = computed(
    () => this.state().selectedWorkplace);
  currentLevelSignal = computed(() => this.state().currentLevel)

  constructor() {
    this.selectedProductionLine$.subscribe((selectedProductionLine) =>
      this.state.update((state) => ({
        ...state,
        selectedProductionLine: selectedProductionLine
          ? {
            id: selectedProductionLine.id,
            name: selectedProductionLine.name,
          }
          : null,
      }))
    );

    this.selectedWorkplace$.subscribe((selectedWorkplace) =>
      this.state.update((state) => ({
        ...state,
        selectedWorkplace: selectedWorkplace
          ? {
            id: selectedWorkplace.id,
            name: selectedWorkplace.name
          }
          : null,
      }))
    )
  }

  public setCurrentLevel(level: Level) {
    this.currentLevel.set(level);
    this.state.update((state) => ({
      ...state,
      currentLevel: level
    }))
  }

  public hasAccessToLevel(level: Level) {
    if (this.userrole() === Userrole.Mitarbeiter) {
      return level === 'productionLine';
    } else {
      return true
    }
  }
}
